import React, { useRef, useState } from 'react'
import { FieldLabel } from '../FieldLabel/FieldLabel'
import infoModal from '../InfoModal/InfoModal'
import Tooltip from '../Tooltip/Tooltip'
import Icon, { IconSize, IconType } from '../Icon2/Icon'
import './AppSwitcher.css'
import Line from '../Line/Line'

export type Product = {
  title: string
  logo: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onMouseOver?: () => React.MouseEvent<HTMLDivElement>
  isActive?: boolean
  testId?: string
}

export type AppSwitcherProps = {
  categories: Category[]
  toolbarIconName: IconType
  toolbarIconSize: IconSize
  toolbarHoverText: string
  testId?: string
}

export type AppSwitcherBodyProps = {
  categories: Category[]
  closeModal: Function
  testId?: string
}

export type AppSwitcherToolbarProps = {
  iconName: IconType
  iconSize: IconSize
  isAppSwitcherOpen: boolean
  hoverText: string
}

export type Category = {
  title?: string
  items: Product[]
  showSeparator?: boolean;
  addRowGap?: boolean;
  closeModal: Function
  testId?: string
}

const Category = ({ title, items, showSeparator, addRowGap, closeModal, testId = "app-switcher-category" }: Category) => {
  return (
    <>
      <div data-test-id={testId} className='app__switcher__category'>
        {title && <FieldLabel testId='app-switcher-category-title' version="v2" className="category__title" htmlFor="app-swicther">
          {title}
        </FieldLabel>}
        <div className={`category__products ${addRowGap ? "with__row__gap" : ""}`}>
          {items.map((val) => (
            <Product
              key={val.title}
              title={val.title}
              logo={val.logo}
              onClick={(e) => {
                closeModal();
                val.onClick(e)
              }}
              onMouseOver={val.onMouseOver}
              isActive={val.isActive}
              testId={val.testId}
            />
          ))}
        </div>
      </div>
      {showSeparator && <Line type='solid' className='category__separator' />}
    </>
  )
}

const Product = ({ title, logo, onClick, onMouseOver, isActive, testId = 'app-switcher-product' }: Product) => {
  return (
    <div className="product" onClick={onClick} onMouseOver={onMouseOver} data-test-id={testId}>
      <div className={`item ${isActive ? 'active' : ''}`}>
        <div>{logo}</div>
        <FieldLabel testId='app-switcher-product-title' version="v2" className="product__title" htmlFor="product">
          {title}
        </FieldLabel>
      </div>
    </div>
  )
}

const AppSwitcherBody = ({ closeModal, categories, testId = "app-switcher-body" }: AppSwitcherBodyProps) => {
  return (
    <div className="app__switcher__body" data-test-id={testId}>
      {categories.map((category) => (
        <Category key={category.title} title={category.title} items={category.items} showSeparator={category.showSeparator} addRowGap={category.addRowGap} closeModal={closeModal} />
      ))}
    </div>
  )
}

const AppSwitcherToolbar = ({ hoverText, iconName, iconSize, isAppSwitcherOpen }: AppSwitcherToolbarProps) => {
  return (
    <Tooltip
      interactive={false}
      variantType="menuIconBasic"
      showArrow={false}
      content={{
        text: hoverText
      }}
      position="bottom"
      disabled={isAppSwitcherOpen}
      testId='app-switcher-toolbar'
    >
      <Icon style={{ cursor: 'pointer' }} icon={iconName} size={iconSize || 'original'} />
    </Tooltip>
  )
}

const AppSwitcher = ({
  categories,
  toolbarIconName,
  toolbarIconSize,
  toolbarHoverText,
  testId = 'app-switcher'
}: AppSwitcherProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const appSwitcherRef = useRef(null)

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true)
      infoModal({
        component: (props: any) => <AppSwitcherBody closeModal={props.closeModal} categories={categories} />,
        modalProps: {
          targetNodeOrId: appSwitcherRef?.current,
          closeOnOverlayClick: true,
          onClose: closeAppSwitcher,
          wrapperStyle: {
            borderRadius: '4px'
          },
          className: "app-switcher-modal"
        },
        alignment: 'bottom-center',
      })
    }
  }

  const closeAppSwitcher = () => {
    setIsOpen(false)
  }

  return (
    <div ref={appSwitcherRef} onClick={handleOpen} className="app__switcher" data-test-id={testId}>
      <AppSwitcherToolbar
        hoverText={toolbarHoverText}
        iconName={toolbarIconName}
        iconSize={toolbarIconSize}
        isAppSwitcherOpen={isOpen}
      />
    </div>
  )
}

export default AppSwitcher